/**
 * Sprint narrative — the OPTIONAL AI-ASSIST layer for Sprint Insights and the
 * Sprint Completion summary.
 *
 * The numbers are ALWAYS computed locally (see {@link ./sprintInsights} and
 * {@link ./sprintCompletion}); this helper only asks the real Bedrock converse
 * op to turn those already-computed numbers into a short, plain-language
 * narrative. The model is explicitly told to reason ONLY over the figures it is
 * given and never invent data — mirroring the AI-STRUCTURE → HUMAN-VERIFY
 * posture used across the app. If inference fails, callers simply omit the prose
 * and show the numbers.
 *
 * Strict layering: a pure feature-level helper that receives the
 * {@link InferenceApi} (via the facade) from the hook; it never touches fetch.
 */

import type { InferenceApi } from '../../../api';
import { ESTIMATE_MODEL_ID } from '../estimate/aiEstimate';
import type { SprintInsights } from './sprintInsights';
import type { SprintCompletionSummary } from './sprintCompletion';

const NARRATIVE_MAX_TOKENS = 320;
const NARRATIVE_TEMPERATURE = 0.2;

const SYSTEM_PROMPT = [
  'You are an engineering delivery lead writing a SHORT, plain-language sprint',
  'note for the team. You are given a set of ALREADY-COMPUTED sprint numbers.',
  'Write 2–4 sentences that interpret ONLY those numbers — do not invent tasks,',
  'names, dates, or figures that are not provided. Be concrete and calm; call out',
  'the single most important thing to do next. No markdown, no headings, no bullet',
  'lists — just a short paragraph.',
].join('\n');

/** Builds a short narrative for the live Sprint Insights panel. */
export async function insightsNarrative(
  inference: InferenceApi,
  sprintName: string,
  insights: SprintInsights,
): Promise<string> {
  const prompt = [
    `Sprint: ${sprintName || 'current sprint'}`,
    `Health: ${insights.health}`,
    `Committed effort: ${insights.committedEffortDays}d`,
    `Completed effort: ${insights.completedEffortDays}d (${insights.percentComplete}%)`,
    `Remaining effort: ${insights.remainingEffortDays}d`,
    `Remaining team capacity: ${insights.remainingCapacityDays}d`,
    `Blocked tasks: ${insights.blockedCount} (at-risk: ${insights.atRiskCount})`,
    insights.blocked.length > 0
      ? `Longest blocker: “${insights.blocked[0].title}” for ${insights.blocked[0].days} day(s)`
      : 'No blocked tasks.',
    '',
    'Write the short sprint note now:',
  ].join('\n');

  const response = await inference.converse({
    modelId: ESTIMATE_MODEL_ID,
    systemPrompt: SYSTEM_PROMPT,
    prompt,
    maxTokens: NARRATIVE_MAX_TOKENS,
    temperature: NARRATIVE_TEMPERATURE,
  });
  return (response.outputText ?? '').trim();
}

/** Builds a short narrative for the end-of-sprint completion summary. */
export async function completionNarrative(
  inference: InferenceApi,
  sprintName: string,
  summary: SprintCompletionSummary,
): Promise<string> {
  const acc = summary.accuracy;
  const accLine =
    acc.bias === 'no_data'
      ? 'Estimate accuracy: no historical data yet.'
      : `Estimate accuracy: actual/estimate ratio ${acc.ratio} (${acc.bias.replace('_', '-')}), over ${acc.sampleSize} record(s).`;
  const prompt = [
    `Sprint: ${sprintName || 'the sprint'} (now complete)`,
    `Committed effort: ${summary.committedEffortDays}d`,
    `Completed effort: ${summary.completedEffortDays}d`,
    `Carryover effort: ${summary.carryoverEffortDays}d`,
    `Tasks completed: ${summary.tasksCompleted}/${summary.tasksTotal}`,
    `Blocked at close: ${summary.blockedTasks}`,
    `Goals advanced: ${summary.goalsAdvanced}`,
    accLine,
    '',
    'Write a short, honest retrospective note (what went well, what to improve next sprint):',
  ].join('\n');

  const response = await inference.converse({
    modelId: ESTIMATE_MODEL_ID,
    systemPrompt: SYSTEM_PROMPT,
    prompt,
    maxTokens: NARRATIVE_MAX_TOKENS,
    temperature: NARRATIVE_TEMPERATURE,
  });
  return (response.outputText ?? '').trim();
}
